// src/hooks/useDocumentos.js — V5 ENTERPRISE ULTRA ROBUSTO
// ═══════════════════════════════════════════════════════════════════════════
// 🧠 HOOK CENTRAL DE DOCUMENTOS — Single Source of Truth
// ═══════════════════════════════════════════════════════════════════════════
// 🔥 V5: OPTIMIZADO — Delete atómico sin parpadeo (isDeletingRef unificado)
// 🔥 V5: OPTIMIZADO — Bulk delete con Promise.all (paralelo, no secuencial)
// 🔥 V5: OPTIMIZADO — Rollback instantáneo desde memoria (sin read extra)
// 🔥 V5: OPTIMIZADO — Observer bloqueado 1.5s post-delete (anti-restauración)
// 🔥 V4: CORREGIDO — DataContext.getInstance() es SÍNCRONO (sin await)
// 🔥 V4: CORREGIDO — mountedRef.current = true al inicio del efecto (StrictMode)
// ═══════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/useAuth';
import { useNotifications } from '../contexts/NotificationsContext';
import DataContext from '../lib/data/context/DataContext';

// ═══════════════════════════════════════════════════════════════════════════
// 📐 CONSTANTES
// ═══════════════════════════════════════════════════════════════════════════

const ALLOWED_FILE_TYPES = {
  'application/pdf': { icon: 'FileText', color: 'text-red-500', label: 'PDF' },
  'image/jpeg': { icon: 'Image', color: 'text-blue-500', label: 'JPEG' },
  'image/png': { icon: 'Image', color: 'text-green-500', label: 'PNG' },
  'image/gif': { icon: 'Image', color: 'text-purple-500', label: 'GIF' },
  'application/msword': { icon: 'FileText', color: 'text-blue-700', label: 'DOC' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { icon: 'FileText', color: 'text-blue-700', label: 'DOCX' },
  'application/vnd.ms-excel': { icon: 'FileText', color: 'text-green-700', label: 'XLS' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { icon: 'FileText', color: 'text-green-700', label: 'XLSX' },
};

const DEFAULT_FILE_TYPE = { icon: 'File', color: 'text-slate-500', label: 'ARQUIVO' };

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const INIT_TIMEOUT_MS = 15000;
const MAX_INIT_RETRIES = 2;
const DELETE_OBSERVER_BLOCK_MS = 1500; // 🔥 V5: Bloquear observer post-delete

// ═══════════════════════════════════════════════════════════════════════════
// 🛠️ UTILITARIO: Deduplicación
// ═══════════════════════════════════════════════════════════════════════════

function deduplicateDocuments(docs) {
  if (!Array.isArray(docs)) return [];
  const seen = new Map();
  const result = [];
  for (const doc of docs) {
    const key = doc.id || doc._id;
    if (!key) { result.push(doc); continue; }
    const existing = seen.get(key);
    if (!existing) {
      seen.set(key, doc);
      result.push(doc);
    } else if (!doc._optimistic && existing._optimistic) {
      const idx = result.indexOf(existing);
      if (idx !== -1) { result[idx] = doc; seen.set(key, doc); }
    }
  }
  return result;
}

// ═══════════════════════════════════════════════════════════════════════════
// 🧠 HOOK PRINCIPAL — V5
// ═══════════════════════════════════════════════════════════════════════════

export function useDocumentos() {
  const { user } = useAuth();
  const { showToast, addNotification } = useNotifications();

  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false); // 🔥 V5: Estado de eliminación para UI

  const [documents, setDocuments] = useState([]);
  const [clients, setClients] = useState([]);
  const [properties, setProperties] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, doc: null });

  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterClient, setFilterClient] = useState('all');
  const [filterProperty, setFilterProperty] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showFilters, setShowFilters] = useState(true);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [showChart, setShowChart] = useState(true);

  const [form, setForm] = useState({
    id: null, title: '', description: '', fileName: '', fileType: '',
    base64: '', clientId: '', propertyId: '', tags: [], size: 0,
  });

  const ctxRef = useRef(null);
  const fileInputRef = useRef(null);
  const searchInputRef = useRef(null);
  const mountedRef = useRef(true);
  const retryCountRef = useRef(0);
  const loadTimerRef = useRef(null);
  const observerUnsubscribeRef = useRef(null);
  const isSavingRef = useRef(false);
  // 🔥 V5: Bandera unificada anti-parpadeo (save + delete + observer)
  const isMutatingRef = useRef(false);
  const deleteTimerRef = useRef(null);

  // ═══════════════════════════════════════════════════════════════════════
  // 🏗️ INICIALIZACIÓN ÚNICA — V5
  // ═══════════════════════════════════════════════════════════════════════

  useEffect(() => {
    let cancelled = false;
    mountedRef.current = true;

    const loadData = async () => {
      const ctx = ctxRef.current;
      if (!ctx || cancelled) return;
      setLoading(true);

      loadTimerRef.current = setTimeout(() => {
        if (!cancelled && mountedRef.current) {
          console.error('⏰ [useDocumentos V5] Timeout cargando datos');
          setError('Tempo limite excedido ao carregar documentos');
          setLoading(false);
        }
      }, INIT_TIMEOUT_MS);

      try {
        const [docs, clientsData, propsData] = await Promise.all([
          ctx.collections.documents.read().catch(() => []),
          ctx.collections.clients.read().catch(() => []),
          ctx.collections.properties.read().catch(() => []),
        ]);
        if (cancelled) return;
        clearTimeout(loadTimerRef.current);
        setDocuments(deduplicateDocuments(Array.isArray(docs) ? docs : []));
        setClients(Array.isArray(clientsData) ? clientsData : []);
        setProperties(Array.isArray(propsData) ? propsData : []);
        setError(null);
        retryCountRef.current = 0;
        console.log(`📂 [useDocumentos V5] Carga inicial: ${documents.length} docs`);
      } catch (err) {
        if (cancelled) return;
        clearTimeout(loadTimerRef.current);
        console.error('❌ [useDocumentos V5] Error cargando datos:', err);
        if (retryCountRef.current < MAX_INIT_RETRIES) {
          retryCountRef.current++;
          console.log(`🔄 [useDocumentos V5] Reintentando (${retryCountRef.current}/${MAX_INIT_RETRIES})...`);
          await new Promise(r => setTimeout(r, 1500));
          if (!cancelled) { loadData(); return; }
        }
        setError(err.message || 'Erro ao carregar documentos');
      } finally {
        if (!cancelled && mountedRef.current) setLoading(false);
      }
    };

    const init = () => {
      if (!user?.uid) { setLoading(false); setError('Usuário não autenticado'); return; }
      const ctx = DataContext.getInstance(user.uid);
      ctxRef.current = ctx;
      console.log('✅ [useDocumentos V5] DataContext obtenido — userId:', user.uid);

      if (typeof ctx.subscribe === 'function') {
        observerUnsubscribeRef.current = ctx.subscribe((newState) => {
          if (!mountedRef.current || !newState) return;
          // 🔥 V5: Bloquear observer durante mutaciones (save, delete)
          if (isMutatingRef.current) return;

          if (Array.isArray(newState.documents)) {
            const deduped = deduplicateDocuments(newState.documents);
            setDocuments(prev => {
              if (prev.length !== deduped.length) return deduped;
              const prevIds = new Set(prev.map(d => d.id || d._id).filter(Boolean));
              const newIds = new Set(deduped.map(d => d.id || d._id).filter(Boolean));
              if (prevIds.size === newIds.size && [...prevIds].every(id => newIds.has(id))) {
                const hasChanges = deduped.some((newDoc, i) => {
                  const prevDoc = prev[i];
                  if (!prevDoc) return true;
                  return JSON.stringify(prevDoc) !== JSON.stringify(newDoc);
                });
                if (!hasChanges) return prev;
              }
              return deduped;
            });
          }
          if (Array.isArray(newState.clients)) {
            setClients(prev => prev.length !== newState.clients.length ? newState.clients : prev);
          }
          if (Array.isArray(newState.properties)) {
            setProperties(prev => prev.length !== newState.properties.length ? newState.properties : prev);
          }
        });
        console.log('👁️ [useDocumentos V5] Observer registrado');
      }
      loadData();
    };

    init();

    return () => {
      cancelled = true;
      if (loadTimerRef.current) clearTimeout(loadTimerRef.current);
      if (deleteTimerRef.current) clearTimeout(deleteTimerRef.current);
      if (typeof observerUnsubscribeRef.current === 'function') {
        observerUnsubscribeRef.current();
      }
    };
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // ═══════════════════════════════════════════════════════════════════════
  // 📊 ESTADÍSTICAS + GRÁFICO
  // ═══════════════════════════════════════════════════════════════════════

  const stats = useMemo(() => {
    const images = documents.filter(d => d.fileType?.startsWith('image/')).length;
    const pdfs = documents.filter(d => d.fileType === 'application/pdf').length;
    const largeDocs = documents.filter(d => d.storage === 'indexeddb' || d.hasLargeFile).length;
    const totalSize = documents.reduce((sum, d) => sum + (d.size || 0), 0);
    return { total: documents.length, images, pdfs, largeDocs, totalSize, totalSizeMB: (totalSize / (1024 * 1024)).toFixed(2) };
  }, [documents]);

  const chartData = useMemo(() => {
    const other = stats.total - stats.images - stats.pdfs - stats.largeDocs;
    return [
      { label: 'Imagens', value: stats.images, color: '#10b981' },
      { label: 'PDFs', value: stats.pdfs, color: '#ef4444' },
      { label: 'Documentos', value: Math.max(0, other), color: '#3b82f6' },
      { label: 'Cloud', value: stats.largeDocs, color: '#8b5cf6' },
    ].filter(item => item.value > 0);
  }, [stats]);

  const chartGrowth = useMemo(() => ({ percentage: 0, trend: 'stable', formatted: '0%' }), []);

  // ═══════════════════════════════════════════════════════════════════════
  // 🔍 FILTROS Y ORDENAMIENTO
  // ═══════════════════════════════════════════════════════════════════════

  const filteredAndSortedDocs = useMemo(() => {
    let result = [...documents];
    if (search?.trim()) {
      const term = search.toLowerCase().trim();
      result = result.filter(d => d.title?.toLowerCase().includes(term) || d.fileName?.toLowerCase().includes(term) || d.description?.toLowerCase().includes(term));
    }
    if (filterType !== 'all') {
      const filterMap = {
        image: d => d.fileType?.startsWith('image/'),
        pdf: d => d.fileType === 'application/pdf',
        document: d => d.fileType?.includes('word') || d.fileType?.includes('document') || d.fileType?.includes('text') || d.fileType?.includes('excel') || d.fileType?.includes('spreadsheet'),
        large: d => d.storage === 'indexeddb' || d.hasLargeFile,
      };
      if (filterMap[filterType]) result = result.filter(filterMap[filterType]);
    }
    if (filterClient !== 'all') result = result.filter(d => d.clientId === filterClient);
    if (filterProperty !== 'all') result = result.filter(d => d.propertyId === filterProperty);
    result.sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'date') { comparison = (a.createdAt ? new Date(a.createdAt).getTime() : 0) - (b.createdAt ? new Date(b.createdAt).getTime() : 0); }
      else if (sortBy === 'title') { comparison = (a.title || '').localeCompare(b.title || ''); }
      else if (sortBy === 'size') { comparison = (a.size || 0) - (b.size || 0); }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    return result;
  }, [documents, search, filterType, filterClient, filterProperty, sortBy, sortOrder]);

  // ═══════════════════════════════════════════════════════════════════════
  // 🖼️ UTILITARIOS DE ARCHIVO
  // ═══════════════════════════════════════════════════════════════════════

  const getFileTypeInfo = useCallback((fileType) => ALLOWED_FILE_TYPES[fileType] || DEFAULT_FILE_TYPE, []);
  const formatFileSize = useCallback((bytes) => {
    if (!bytes) return '0 KB';
    const kb = bytes / 1024;
    return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`;
  }, []);

  const compressImage = useCallback((base64, maxWidth = 1024) => new Promise((resolve) => {
    const img = new Image();
    img.src = base64;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let w = img.width, h = img.height;
      if (w > maxWidth) { h = (maxWidth * h) / w; w = maxWidth; }
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', 0.8));
    };
    img.onerror = () => resolve(base64);
  }), []);

  const handleFileChange = useCallback(async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) { showToast?.('Arquivo muito grande. Máximo: 10MB', 'error', { title: '❌ Tamanho excedido' }); return; }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        let base64 = event.target.result;
        if (file.type.startsWith('image/') && file.size > 2 * 1024 * 1024) base64 = await compressImage(base64);
        setForm(prev => ({ ...prev, fileName: file.name, fileType: file.type, base64, size: base64.length }));
        showToast?.(`Arquivo "${file.name}" carregado`, 'success', { title: '📤 Upload concluído' });
      };
      reader.onerror = () => showToast?.('Erro ao ler o arquivo', 'error', { title: '❌ Falha no upload' });
      reader.readAsDataURL(file);
    } catch (err) { showToast?.('Erro ao processar arquivo', 'error', { title: '❌ Erro no upload' }); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  }, [compressImage, showToast]);

  // ═══════════════════════════════════════════════════════════════════════
  // 💾 CREAR / EDITAR
  // ═══════════════════════════════════════════════════════════════════════

  const openNewModal = useCallback(() => {
    setForm({ id: null, title: '', description: '', fileName: '', fileType: '', base64: '', clientId: '', propertyId: '', tags: [], size: 0 });
    setShowModal(true);
  }, []);

  const handleSave = useCallback(async () => {
    const ctx = ctxRef.current;
    if (!ctx) { showToast?.('Erro de conexão', 'error', { title: '❌ Erro ao salvar' }); return; }
    if (!form.title?.trim()) { showToast?.('Título é obrigatório', 'error', { title: '❌ Campo obrigatório' }); return; }
    if (!form.base64) { showToast?.('Selecione um arquivo', 'error', { title: '❌ Arquivo necessário' }); return; }

    const docData = {
      title: form.title.trim(), description: form.description?.trim() || '',
      fileName: form.fileName, fileType: form.fileType, base64: form.base64,
      size: form.base64.length, clientId: form.clientId || null,
      propertyId: form.propertyId || null, tags: form.tags || [],
    };

    isMutatingRef.current = true;
    try {
      if (form.id) {
        setDocuments(prev => prev.map(d => d.id === form.id ? { ...d, ...docData, updatedAt: new Date().toISOString() } : d));
        await ctx.collections.documents.update(form.id, { ...docData, updatedAt: new Date().toISOString() });
        showToast?.(`Documento "${form.title}" atualizado`, 'success', { title: '✏️ Documento atualizado' });
      } else {
        const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        setDocuments(prev => [{ ...docData, id: tempId, _optimistic: true, createdAt: new Date().toISOString() }, ...prev]);
        const created = await ctx.collections.documents.create({ ...docData, createdAt: new Date().toISOString() });
        const realId = created?.id || created?._id || tempId;
        setDocuments(prev => prev.map(d => d.id === tempId ? { ...docData, id: realId, _optimistic: false, createdAt: docData.createdAt || new Date().toISOString(), updatedAt: created?.updatedAt || new Date().toISOString() } : d));
        showToast?.(`Documento "${form.title}" criado`, 'success', { title: '✅ Documento salvo' });
        addNotification?.({ type: 'success', title: 'Novo documento', message: `${form.title} · ${form.fileName}`, metadata: { clientId: form.clientId, propertyId: form.propertyId, fileType: form.fileType, timestamp: new Date().toISOString() } });
      }
      setShowModal(false);
    } catch (err) {
      console.error('❌ [V5] Error al guardar:', err);
      showToast?.(err.message || 'Erro ao salvar', 'error', { title: '❌ Falha no salvamento' });
    } finally {
      setTimeout(() => { isMutatingRef.current = false; }, 300);
    }
  }, [form, showToast, addNotification]);

  // ═══════════════════════════════════════════════════════════════════════
  // 👁️ PREVIEW + DOWNLOAD
  // ═══════════════════════════════════════════════════════════════════════

  const handlePreview = useCallback(async (doc) => {
    setPreviewLoading(true);
    setPreviewDoc({ ...doc, loading: true });
    try {
      if (doc.base64) { setPreviewDoc(doc); return; }
      if (doc.storage === 'indexeddb' || doc.hasLargeFile) {
        const ctx = ctxRef.current;
        if (!ctx) throw new Error('DataContext não disponível');
        const result = await ctx.collections.documents.read({ id: doc.id });
        const fullDoc = Array.isArray(result) ? result[0] : result;
        if (fullDoc?.base64) { setPreviewDoc(fullDoc); } else { throw new Error('Documento sem conteúdo'); }
      } else { setPreviewDoc(doc); }
    } catch (err) {
      console.error('❌ [V5] Error en preview:', err);
      showToast?.(err.message || 'Erro ao carregar documento', 'error');
      setPreviewDoc(null);
    } finally { setPreviewLoading(false); }
  }, [showToast]);

  const handleDownload = useCallback(async (doc) => {
    try {
      let docToDownload = doc;
      if ((doc.storage === 'indexeddb' || doc.hasLargeFile) && !doc.base64) {
        const ctx = ctxRef.current;
        if (!ctx) throw new Error('DataContext não disponível');
        const result = await ctx.collections.documents.read({ id: doc.id });
        docToDownload = Array.isArray(result) ? result[0] : result;
      }
      if (!docToDownload?.base64) throw new Error('Documento não encontrado');
      const link = document.createElement('a');
      link.href = docToDownload.base64;
      link.download = docToDownload.fileName || 'documento';
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      showToast?.(`Download de "${doc.title}" iniciado`, 'success', { title: '📥 Download' });
    } catch (err) {
      console.error('❌ [V5] Error en download:', err);
      showToast?.(err.message || 'Erro ao fazer download', 'error', { title: '❌ Falha no download' });
    }
  }, [showToast]);

  // ═══════════════════════════════════════════════════════════════════════
  // 🗑️ DELETE — V5 ULTRA OPTIMIZADO
  // ═══════════════════════════════════════════════════════════════════════
  //
  // Estrategia:
  //   1. Bloquear observer (isMutatingRef) → sin parpadeo
  //   2. Optimistic UI → desaparición instantánea
  //   3. Delete en backend → async, no bloquea
  //   4. Si falla → rollback desde memoria (sin read extra)
  //   5. Desbloquear observer tras 1.5s → estado limpio
  // ═══════════════════════════════════════════════════════════════════════

  const handleDelete = useCallback((doc) => {
    setDeleteModal({ isOpen: true, doc });
  }, []);

  const confirmDelete = useCallback(async () => {
    const doc = deleteModal.doc;
    if (!doc) return;
    const ctx = ctxRef.current;
    if (!ctx) { showToast?.('Erro de conexão', 'error', { title: '❌ Erro ao excluir' }); return; }

    // 🔥 V5: Backup desde memoria para rollback instantáneo
    const backup = documents.find(d => d.id === doc.id);

    // 🔥 V5: Bloquear observer ANTES de tocar el estado
    isMutatingRef.current = true;
    setDeleting(true);

    // 🔥 V5: Optimistic UI — eliminar inmediatamente
    setDocuments(prev => prev.filter(d => d.id !== doc.id));
    setSelectedDocs(prev => prev.filter(id => id !== doc.id));
    setDeleteModal({ isOpen: false, doc: null });

    try {
      await ctx.collections.documents.delete(doc.id);
      showToast?.(`Documento "${doc.title}" excluído`, 'success', { title: '🗑️ Documento removido' });
      addNotification?.({ type: 'info', title: 'Documento excluído', message: `${doc.title} · ${doc.fileName}`, metadata: { documentId: doc.id, timestamp: new Date().toISOString() } });
    } catch (err) {
      console.error('❌ [V5] Error al eliminar:', err);
      // 🔥 V5: Rollback instantáneo desde memoria
      if (backup) {
        setDocuments(prev => {
          if (prev.find(d => d.id === backup.id)) return prev;
          return [...prev, backup];
        });
      }
      showToast?.(err.message || 'Erro ao excluir', 'error', { title: '❌ Falha na exclusão' });
    } finally {
      // 🔥 V5: Desbloquear observer tras delay para asegurar sincronización
      if (deleteTimerRef.current) clearTimeout(deleteTimerRef.current);
      deleteTimerRef.current = setTimeout(() => {
        isMutatingRef.current = false;
        setDeleting(false);
      }, DELETE_OBSERVER_BLOCK_MS);
    }
  }, [deleteModal.doc, documents, showToast, addNotification]);

  // ═══════════════════════════════════════════════════════════════════════
  // ⭐ FAVORITOS
  // ═══════════════════════════════════════════════════════════════════════

  const toggleFavorite = useCallback(async (docId, currentValue) => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    setDocuments(prev => prev.map(d => (d.id === docId ? { ...d, isFavorite: !currentValue } : d)));
    try {
      await ctx.collections.documents.update(docId, { isFavorite: !currentValue });
      showToast?.(!currentValue ? 'Adicionado aos favoritos' : 'Removido dos favoritos', 'success', { title: '⭐ Favoritos' });
    } catch (err) {
      setDocuments(prev => prev.map(d => (d.id === docId ? { ...d, isFavorite: currentValue } : d)));
    }
  }, [showToast]);

  // ═══════════════════════════════════════════════════════════════════════
  // ✅ SELECCIÓN MÚLTIPLE
  // ═══════════════════════════════════════════════════════════════════════

  const toggleSelect = useCallback((docId) => {
    setSelectedDocs(prev => prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]);
  }, []);

  const selectAll = useCallback(() => {
    if (selectedDocs.length === filteredAndSortedDocs.length) setSelectedDocs([]);
    else setSelectedDocs(filteredAndSortedDocs.map(d => d.id));
  }, [filteredAndSortedDocs, selectedDocs.length]);

  // ═══════════════════════════════════════════════════════════════════════
  // 🗑️ BULK DELETE — V5 PARALELO
  // ═══════════════════════════════════════════════════════════════════════

  const bulkDelete = useCallback(async () => {
    if (selectedDocs.length === 0) return;
    const ctx = ctxRef.current;
    if (!ctx) { showToast?.('Erro de conexão', 'error', { title: '❌ Erro ao excluir' }); return; }

    const idsToDelete = new Set(selectedDocs);
    const backup = documents.filter(d => idsToDelete.has(d.id));
    const count = selectedDocs.length;

    // 🔥 V5: Bloquear observer
    isMutatingRef.current = true;
    setDeleting(true);

    // 🔥 V5: Optimistic UI instantáneo
    setDocuments(prev => prev.filter(d => !idsToDelete.has(d.id)));
    setSelectedDocs([]);

    try {
      // 🔥 V5: Promise.all paralelo en lugar de for secuencial
      await Promise.all(selectedDocs.map(docId => ctx.collections.documents.delete(docId)));

      showToast?.(`${count} documento(s) excluído(s)`, 'success', { title: '🗑️ Exclusão em massa' });
      addNotification?.({ type: 'warning', title: 'Exclusão em massa', message: `${count} documentos removidos`, metadata: { count, timestamp: new Date().toISOString() } });
    } catch (err) {
      console.error('❌ [V5] Error en bulk delete:', err);
      // 🔥 V5: Rollback instantáneo desde memoria
      if (backup.length > 0) {
        setDocuments(prev => {
          const currentIds = new Set(prev.map(d => d.id));
          const toRestore = backup.filter(b => !currentIds.has(b.id));
          return [...toRestore, ...prev];
        });
      }
      showToast?.(err.message || 'Erro ao excluir documentos', 'error', { title: '❌ Falha na exclusão' });
    } finally {
      if (deleteTimerRef.current) clearTimeout(deleteTimerRef.current);
      deleteTimerRef.current = setTimeout(() => {
        isMutatingRef.current = false;
        setDeleting(false);
      }, DELETE_OBSERVER_BLOCK_MS);
    }
  }, [selectedDocs, documents, showToast, addNotification]);

  // ═══════════════════════════════════════════════════════════════════════
  // 🔄 RESET FILTROS
  // ═══════════════════════════════════════════════════════════════════════

  const resetFilters = useCallback(() => {
    setSearch(''); setFilterType('all'); setFilterClient('all');
    setFilterProperty('all'); setSortBy('date'); setSortOrder('desc');
  }, []);

  // ═══════════════════════════════════════════════════════════════════════
  // 📤 RETORNO
  // ═══════════════════════════════════════════════════════════════════════

  return {
    documents, clients, properties, stats, chartData, chartGrowth,
    filteredAndSortedDocs, loading, error, deleting,
    ctxReady: !loading && !error,
    showModal, setShowModal, previewDoc, setPreviewDoc, previewLoading,
    deleteModal, setDeleteModal, showChart, setShowChart, showFilters, setShowFilters, selectedDocs,
    search, setSearch, filterType, setFilterType, filterClient, setFilterClient,
    filterProperty, setFilterProperty, sortBy, setSortBy, sortOrder, setSortOrder, resetFilters,
    form, setForm, uploading,
    fileInputRef, searchInputRef,
    openNewModal, handleSave, handleFileChange, handlePreview, handleDownload,
    handleDelete, confirmDelete, toggleFavorite, toggleSelect, selectAll, bulkDelete,
    getFileTypeInfo, formatFileSize,
  };
}

export default useDocumentos;